/**
 * @file INCSUBPlatformAndPools.js
 * @description
 * This component displays the list of platforms and pool details associated with a specific user.
 * It fetches user pool/platform data based on the user's ID and presents it in a table format.
 * Handles unauthorized access by redirecting to the login page.
 *
 * @props {Object} personalDetailData - Contains user details, specifically userId.
 *
 * @dependencies
 * - React
 * - React Router for navigation
 * - Custom services: getUserPlatforms, getUserPoolsDetails
 * - Toast notifications
 * - CSS: stylePlatformAndPools.css
 * @author govind dindewad
 */

import React, { useState, useEffect } from 'react';
import './stylePlatformAndPools.css';
import { getUserPlatforms } from '../../services/userServices';
import { toast } from 'react-toast';
import { useNavigate } from 'react-router';

function INCSUBPlatformAndPools({ personalDetailData }) {
    const [userPoolDetailData, setUserPoolDetailData] = useState(null);
    const navigate = useNavigate();

    // Fetch pool and platform data for the user
    const fetchPoolsDetailsData = async () => {
        try {
            const response = await getUserPlatforms(personalDetailData?.userId, 0);
            if (response.status === 200) {
                // Set the fetched data to state
                setUserPoolDetailData(response?.data?.data);
            } else if (response.status === 401) {
                // Redirect to login on unauthorized access
                navigate('/login');
                window.localStorage.setItem('userData', null);
            } else {
                // Show other error messages
                toast.error(response.data.message);
            }
        } catch (err) {
            toast.error('Something Went Wrong');
        }
    };

    // Trigger data fetch on component mount
    useEffect(() => {
        if (personalDetailData) {
            fetchPoolsDetailsData();
        }
    }, []);

    return (
        <div className='INCSUBplatformandpools'>
            <div className='INCSUBplatformandpools_container'>
                {/* Header */}
                <div className='INCSUBplatformandpools_header_container'>
                    <div className='INCSUBplatformandpools_header'>Platforms & Pools</div>
                </div>

                {/* Table Section */}
                <div className='INCSUBplatformandpools_userlist_section'>
                    <div className='INCSUBplatformandpools_userlist_section_table_conatiner'>
                        {/* Check if there is any data to display */}
                        {userPoolDetailData && userPoolDetailData.platformAndPoolDetailList.length > 0 ? (
                            <table className='INCSUBplatformandpools_table'>
                                <thead className='INCSUBplatformandpools_table_head'>
                                    <tr>
                                        <th>#</th>
                                        <th>Platform Name</th>
                                        <th>Plan</th>
                                        <th>Pool Name</th>
                                        <th>Duration</th>
                                        <th>User Type</th>
                                        <th>Earning</th>
                                    </tr>
                                </thead>
                                <tbody className='INCSUBplatformandpools_table_body'>
                                    {userPoolDetailData.platformAndPoolDetailList.map((pool, index) => (
                                        <tr key={index}>
                                            <td>{index + 1}</td>
                                            <td>{pool.platformName}</td>
                                            <td>{pool.planName}</td>
                                            <td className='INCSUBplatformandpools_poolinfo'>
                                                <div className='INCSUBplatformandpools_poolname'>{pool.poolName}</div>
                                                <div className='INCSUBplatformandpools_pooltype'>{pool.poolType}</div>
                                            </td>
                                            <td>{pool.poolStartDate} - {pool.poolEndDate}</td>
                                            <td>{pool.userType}</td>
                                            <td>₹{pool.earning}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            // No data available fallback
                            <div className='INCSUBUserPlatform_empty'>No Platform and Pools details</div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default INCSUBPlatformAndPools;
